import { ViewController } from 'elc-service';
import { computed, observable, action } from 'mobx';
import { serviceNames, diContainer } from '../../service-setup/diContainer';
import { BrowserStorage, root } from 'elc-universal-utils';
import { events, emit } from 'elc-service-bus';
import { IAsset } from '../domain/entities/Asset';
import { ICarouselSettings, IImageDevice, IImagesSizes } from '../interfaces/IProdcat';
import { ComponentTypes } from '../constants/ComponentTypes';
import { LiveChatComponentTypes } from '../constants/LiveChatComponentTypes';
import { AssetTypes } from '../constants/AssetTypes';
import { DeviceType, DESKTOP, MOBILE } from '../constants/DeviceTypes';
import { Product } from '../domain/entities/Product';
import { Sku } from '../domain/entities/Sku';
import { IVideoAsset } from '../domain/entities/VideoAsset';
import { IMediaType } from '../domain/entities/Media';
import * as queryParamUtils from '../utils/QueryParams';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';

export interface IMediaObject {
    media: IMediaType[];
    defaultImage: IAsset;
}

export interface IImagesForDevice {
    [key: string]: IMediaObject;
}

export class ProductBaseController extends ViewController {
    public static shadeUrlParam = 'shade';
    public static sizeUrlParam = 'size';
    public positionIndex: number;

    @observable product: Product;
    @observable selectedSkuId: string;
    @observable public selectedAutoReplenishOption: number = 0;
    @observable public isAutoReplenishSelected = false;
    @observable public carouselSettings?: ICarouselSettings;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @observable private prodcatConfig: any;

    constructor() {
        super();
        this.prodcatConfig = diContainer.get(serviceNames.config);
    }

    @action
    public setProduct(product: Product) {
        this.product = product;
    }

    @action
    public setSelectedSkuId(skuId: string) {
        this.selectedSkuId = skuId;
    }

    @computed
    public get imagesSizeConfig() {
        const defaultImagesSizesConfig: IImagesSizes = {
            productGrid: {
                desktop: AssetTypes.MEDIUM,
                mobile: AssetTypes.MEDIUM
            },
            productQuickshop: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            },
            productFull: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            }
        };

        return this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imagesSizes
            ? this.prodcatConfig.config.imagesSizes
            : defaultImagesSizesConfig;
    }

    @computed
    public get selectedSku(): Sku {
        return (
            this.product.skus.find((sku) => sku.skuId === this.selectedSkuId) ||
            this.product.defaultSku
        );
    }

    @computed
    get largeImages() {
        let skuImages: IAsset[] = [];
        let productLargeImages: IAsset[] = [];
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.hasLargeImages) {
                [...skuImages] = selectedSku.largeImages;
            }
        }

        if (product && product.hasLargeImages) {
            [...productLargeImages] = product.largeImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productLargeImages]);
    }

    @computed
    get largeMedia() {
        const videoAltEnabled = this.carouselSettings && this.carouselSettings.videoAltEnabled;

        if (!videoAltEnabled) {
            return this.largeImages;
        }

        const media: IMediaType[] = this.addVideoThubnails(
            this.largeImages,
            this.product.media.videos || []
        );

        return media;
    }

    @computed
    get mediumImages() {
        let skuImages: IAsset[] = [];
        let productMediumImages: IAsset[] = [];
        const { product } = this;
        const selectedSku = this.selectedSku;

        if (selectedSku) {
            if (selectedSku.hasMediumImages) {
                [...skuImages] = selectedSku.mediumImages;
            }
        }

        if (product && product.hasMediumImages) {
            [...productMediumImages] = product.mediumImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productMediumImages]);
    }

    @computed
    public get altTextPrefix(): string {
        return this.selectedSku.selectedShade.name || this.selectedSku.selectedSize.value || '';
    }

    @computed
    public get imageCarouselSettings() {
        return (
            this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imageCarouselSettings
        );
    }

    @computed
    public get shortDescriptionSettings() {
        return (
            this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.shortDescription
        );
    }

    private getImagesForDevice(device: DeviceType, imagesSizeConfig?: IImageDevice): IMediaObject {
        const useLargeImages = imagesSizeConfig && imagesSizeConfig[device] === AssetTypes.LARGE;
        const selectedSku = this.selectedSku;

        const media: IMediaType[] = useLargeImages ? this.largeImages : this.mediumImages;
        const defaultImage = useLargeImages
            ? this.enhanceImageAltText(selectedSku.defaultLargeImage)
            : this.enhanceImageAltText(selectedSku.defaultMediumImage);

        return {
            media,
            defaultImage
        };
    }

    private getMediaForDevice(device: DeviceType, imagesSizeConfig?: IImageDevice): IMediaObject {
        const imagesData = this.getImagesForDevice(device, imagesSizeConfig);
        const videoAltEnabled = this.carouselSettings && this.carouselSettings.videoAltEnabled;

        if (!videoAltEnabled) {
            return imagesData;
        }

        const media: IMediaType[] = this.addVideoThubnails(
            imagesData.media,
            this.product.media.videos || []
        );

        return {
            defaultImage: imagesData.defaultImage,
            media
        };
    }

    public getCustomSizeMedia = (component: ComponentTypes): IImagesForDevice => {
        const imagesSizeConfig = this.imagesSizeConfig && this.imagesSizeConfig[component];

        return {
            desktop: this.getMediaForDevice(DESKTOP, imagesSizeConfig),
            mobile: this.getMediaForDevice(MOBILE, imagesSizeConfig)
        };
    };

    public addVideoThubnails(mediaList: IMediaType[], productVideos: IVideoAsset[]) {
        if (!productVideos || !productVideos.length) {
            return mediaList;
        }

        const videoPositioningEnabled =
            this.carouselSettings && this.carouselSettings.videoPositioningEnabled;

        const videoReducer = videoPositioningEnabled
            ? this.insertVideoAtPosition
            : this.insertVideoAtEnd;

        return productVideos.reduce(videoReducer, mediaList);
    }

    private insertVideoAtEnd(mediaList: IMediaType[], productVideo: IVideoAsset) {
        return [...mediaList, productVideo];
    }

    private insertVideoAtPosition(mediaList: IMediaType[], productVideo: IVideoAsset) {
        const _mediaList = [...mediaList];
        let position = productVideo.position != null ? productVideo.position : mediaList.length;

        if (position > mediaList.length) {
            position = mediaList.length;
        }

        _mediaList.splice(position, 0, productVideo);

        return _mediaList;
    }

    @action
    public setMediaSettings = (mediaSettings?: ICarouselSettings) => {
        this.carouselSettings = mediaSettings;
    };

    @action
    public setAutoReplenishOption = (option: number) => {
        this.selectedAutoReplenishOption = option;
    };

    @action
    public setAutoReplenishSelection = (isSelected: boolean) => {
        this.isAutoReplenishSelected = isSelected;
        if (!isSelected) {
            this.setAutoReplenishOption(0);
        }
    };

    @action
    public toggleAutoReplenishSelection = () => {
        this.setAutoReplenishSelection(!this.isAutoReplenishSelected);
    };

    @action
    public selectShade = (name: string) => {
        const clickedShade = this.product.getShadeByName(name);
        if (clickedShade && clickedShade.parentSku) {
            this.selectedSkuId = clickedShade.parentSku.skuId;
        }
    };

    @action
    public selectSize = (label: string) => {
        const clickedSize = this.product.getSizeByLabel(label);
        if (clickedSize && clickedSize.parentSku) {
            this.selectedSkuId = clickedSize.parentSku.skuId;
        }
    };

    public sanitizeSelection = (selection: string) => {
        return selection.replace(/\s/g, ' ').split(' ').join('_');
    };

    public urlUpdateState = (searchKey: string, searchValue: string) => {
        const search = (<Location>root.location).search;
        const pathName = (<Location>root.location).pathname;
        const urlParams = new URLSearchParams(search);
        const sanitizedValue = this.sanitizeSelection(searchValue);
        urlParams.set(searchKey, sanitizedValue);
        window.history.replaceState({}, '', `${pathName}?${urlParams.toString()}`);
    };

    public clearURLState = (searchKey: string) => {
        const search = (<Location>root.location).search;
        const urlParams = new URLSearchParams(search);
        urlParams.delete(searchKey);
        let urlNewState = `${window.location.pathname}`;
        const queryParams = urlParams.toString();
        if (queryParams !== '') {
            urlNewState = urlNewState.concat(`?${queryParams}`);
        }
        window.history.replaceState({}, '', `${urlNewState}`);
    };

    public handleShadeSelect = (shadeName: string) => {
        this.selectShade(shadeName);

        const {
            selectedShade: { name },
            skuId
        } = this.selectedSku;

        emit(events.PRODUCT_SHADE_SELECTED, { skuId });
        this.emitProductSkuSelected();

        if (name) {
            this.urlUpdateState(ProductBaseController.shadeUrlParam, name);
        } else {
            this.clearURLState(ProductBaseController.shadeUrlParam);
        }
    };

    public handleSizeSelect = (label: string) => {
        this.selectSize(label);
        const { value } = this.selectedSku.selectedSize;
        if (value) {
            this.urlUpdateState(ProductBaseController.sizeUrlParam, value);
            this.emitProductSkuSelected();
        } else {
            this.clearURLState(ProductBaseController.sizeUrlParam);
        }
    };

    public emitProductSkuSelected() {
        const { skuId, ingredients } = this.selectedSku;
        emit(events.PRODUCT_SKU_SELECTED, { skuId, payload: { ingredients } });
    }

    public handleFavoriteClick = () => {
        emit(events.FAVORITE_SELECTED, {
            skuBaseId: this.selectedSku.perlgem.SKU_BASE_ID
        });
    };

    private enhanceImageListAltText(images: IAsset[]) {
        let skuImages: IAsset[] = [];
        skuImages = images.map((image, index) => {
            const isFirstImage = !index;
            if (isFirstImage) {
                return this.enhanceImageAltText(image);
            }

            const img = { ...image };
            img.alt = '';

            return img;
        });

        return skuImages;
    }

    private enhanceImageAltText(image: IAsset) {
        const img = { ...image };
        img.alt = `${this.altTextPrefix} | ${img.alt}`.toLowerCase().trim();

        return img;
    }

    @computed
    get selectedProductUrl() {
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.selectedShade && selectedSku.selectedShade.name) {
                return `${product.productUrl}?${
                    ProductBaseController.shadeUrlParam
                }=${encodeURIComponent(selectedSku.selectedShade.name)}`;
            }
            if (selectedSku.selectedSize && selectedSku.selectedSize.value) {
                return `${product.productUrl}?${
                    ProductBaseController.sizeUrlParam
                }=${encodeURIComponent(selectedSku.selectedSize.value)}`;
            }
        }

        return product.productUrl;
    }

    public navigateToProduct = async () => {
        // check for query params and push them into history
        const search = queryParamUtils.getQueryParams();
        if (search.length) {
            window.history.pushState({}, '');
        }
        const positionIndex = this.positionIndex;
        const { skuId } = this.selectedSku;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            this.selectedProductUrl,
            skuId,
            positionIndex
        );
        await navigateToProductUseCase.execute();
    };
    public handleLiveChatCTAClick = (liveChatComponentType: LiveChatComponentTypes) => {
        emit(events.LIVE_CHAT_INTERFACE_CLICKED, {
            eventLabel: liveChatComponentType
        });
    };

    // Auto select the best shade recommended by VTO if the best match exists in cookie
    public selectBestShade(isSPP: boolean = false) {
        if (!this.product.isShaded) {
            return;
        }

        const shadeMatchCookie = BrowserStorage.getItem('vtoMatches');
        if (shadeMatchCookie) {
            const shadeMatches = JSON.parse(shadeMatchCookie);
            const shadeMatch = shadeMatches[this.product.productId];
            const bestSkuId = shadeMatch && shadeMatch.best;
            const bestSku = bestSkuId && this.product.skus.find((sku) => sku.skuId === bestSkuId);

            if (bestSku) {
                this.setSelectedSkuId(bestSku.skuId);
                isSPP && this.handleShadeSelect(bestSku.selectedShade.name);
                bestSku.selectedShade.setBestShade();
            }
        }
    }
}
